import { useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { validateAccountNumber } from "../OrderForm.rules";
import { OrderFormData } from "../OrderForm.schema";
import { NEW_PORTFOLIO_ID } from "./PortfolioSelector";
import { Portfolio } from "@/api/types";

type Props = {
  allPortfolios: Portfolio[];
};

export function AccountSection({ allPortfolios }: Props) {
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext<OrderFormData>();

  const bankAccount = useWatch({ name: "dpAccount" });
  const portfolioId = useWatch({ name: "dpPortfolioId" });
  const isNew = portfolioId === NEW_PORTFOLIO_ID;

  const prevPortfolioIdRef = useRef(portfolioId);

  useEffect(() => {
    if (isNew) {
      setValue("dpWithdrawal", 0);
    }
  }, [isNew, setValue]);

  // Collect all KAR-OK accounts across all portfolios (unique by number)
  const seen = new Set<string>();
  const accountNumbers: string[] = [];
  for (const p of allPortfolios) {
    for (const a of p.accounts) {
      if (a.tagsAsSet.includes("KAR-OK") && !seen.has(a.number)) {
        seen.add(a.number);
        accountNumbers.push(a.number);
      }
    }
  }

  //Set first KAR-OK account as default of the selected portfolio if any
  useEffect(() => {
    if (portfolioId === prevPortfolioIdRef.current) return; // unchanged — skip (handles editing pre-fill)
    prevPortfolioIdRef.current = portfolioId;
    const selectedPortfolio = allPortfolios.find(
      (p) => p.id === Number(portfolioId)
    );
    const karOkAccount = selectedPortfolio?.accounts.find((a) =>
      a.tagsAsSet.includes("KAR-OK")
    );
    if (karOkAccount) {
      setValue("dpAccount", karOkAccount.number);
    } else {
      setValue("dpAccount", "");
    }
  }, [portfolioId, allPortfolios, setValue]);

  return (
    <div className="space-y-3">
      {/* Bank account */}
      <div className="grid grid-cols-[180px_1fr] items-center gap-3">
        <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
          Bank account:
        </label>
        <div className="space-y-1">
        <select
          {...register("dpAccount")}
          className={`w-full rounded-md border ${errors.dpAccount ? "border-red-500" : "border-gray-300 dark:border-gray-600"} bg-white dark:bg-gray-800 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
        >
          <option value="">-- Select --</option>
          <option value="newAccount">New Account</option>
          <option disabled>──────────</option>
          {accountNumbers.map((num) => (
            <option key={num} value={num}>
              {num}
            </option>
          ))}
        </select>
        {errors.dpAccount && (
          <p className="text-xs text-red-500">{errors.dpAccount.message}</p>
        )}
        </div>
      </div>

      {/* New bank account — shown only when "New Account" is selected */}
      {bankAccount === "newAccount" && (
        <div className="grid grid-cols-[180px_1fr] items-center gap-3">
          <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
            New bank account:
          </label>
          <div className="space-y-1">
            <input
              type="text"
              {...register("dpNewAccount", {
                validate: (val) =>
                  !val ||
                  validateAccountNumber(val) ||
                  "Account number is not valid",
              })}
              placeholder="11-digit account number"
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {errors.dpNewAccount && (
              <p className="text-xs text-red-500">
                {errors.dpNewAccount.message}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Withdrawal */}
      <div className="grid grid-cols-[180px_1fr] items-center gap-3">
        <label className="text-sm font-medium text-right text-gray-600 dark:text-gray-400">
          Withdrawal:
        </label>
        <div className="relative group">
          <input
            type="number"
            min={0}
            step={1000}
            disabled={isNew}
            {...register("dpWithdrawal", { valueAsNumber: true })}
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {isNew && (
            <div className="pointer-events-none absolute bottom-full left-0 mb-1.5 hidden group-hover:flex items-center">
              <span className="whitespace-nowrap rounded bg-gray-800 dark:bg-gray-200 px-2 py-1 text-xs text-white dark:text-gray-900 shadow">
                Not applicable for new portfolios
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
